import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { getTickets } from '../../../services/adminService';
import Loader from '../../../components/common/Loader';

const STATUS_CONFIG = {
  'Pending':     { dot: 'bg-amber-500',  pill: 'bg-amber-50 border-amber-200 text-amber-700' },
  'In Progress': { dot: 'bg-blue-500',   pill: 'bg-blue-50 border-blue-200 text-blue-700' },
  'Solved':      { dot: 'bg-emerald-500',pill: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  'Cancelled':   { dot: 'bg-red-500',    pill: 'bg-red-50 border-red-200 text-red-700' },
};

const PRIORITY_CONFIG = {
  'High':   { pill: 'bg-red-50 border-red-200 text-red-600' },
  'Medium': { pill: 'bg-amber-50 border-amber-200 text-amber-600' },
  'Low':    { pill: 'bg-gray-50 border-gray-200 text-gray-500' },
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric'
  });
};

function DashboardSupportTickets() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await getTickets({ page: 1, limit: 5, search: '', status: '' });
      // Handle both wrapped { data: [...] } and direct array responses
      const list =
        response?.data?.tickets ||
        response?.data?.data ||
        response?.data ||
        [];
      setTickets(Array.isArray(list) ? list.slice(0, 5) : []);
    } catch (err) {
      console.error('Failed to fetch support tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Support Tickets</h2>
          {!loading && (
            <p className="text-xs text-gray-400 mt-0.5">{tickets.length} recent ticket{tickets.length !== 1 ? 's' : ''}</p>
          )}
        </div>
        <button
          onClick={() => navigate('/admin/settings?activeTab=query')}
          className="text-xs font-medium text-[#7083FF] hover:text-[#5a6be8] flex items-center gap-1 transition-colors"
        >
          View All <Icon icon="lucide:arrow-right" width="13" />
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center px-6">
          <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
            <Icon icon="lucide:inbox" width="22" className="text-gray-300" />
          </div>
          <p className="text-sm font-medium text-gray-400">No support tickets</p>
          <p className="text-xs text-gray-300 mt-0.5">All tickets will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" style={{ minWidth: '640px' }}>
            <thead>
              <tr className="bg-gray-50/70">
                <th className="px-5 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Activity ID</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">User / Actor</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Submitted</th>
                <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tickets.map((ticket, idx) => {
                const statusCfg = STATUS_CONFIG[ticket.status] || STATUS_CONFIG['Pending'];
                const priorityCfg = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG['Low'];
                return (
                  <tr
                    key={ticket.id || idx}
                    className="hover:bg-gray-50/60 transition-colors"
                  >
                    {/* Activity ID */}
                    <td className="px-5 py-3">
                      <span className="text-xs font-mono font-medium text-gray-600">
                        #{ticket.id ? String(ticket.id).padStart(5, '0') : String(idx + 1).padStart(5, '0')}
                      </span>
                    </td>

                    {/* User / Actor */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold text-indigo-600">
                            {(ticket.user_name || ticket.name || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs text-gray-700 whitespace-nowrap">
                          {ticket.user_name || ticket.name || '—'}
                        </span>
                      </div>
                    </td>

                    {/* Description */}
                    <td className="px-4 py-3 max-w-[200px]">
                      <span className="text-xs text-gray-600 line-clamp-2">
                        {ticket.subject || ticket.description || ticket.issue || '—'}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${statusCfg.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${statusCfg.dot} inline-block`} />
                        {ticket.status || 'Pending'}
                      </span>
                    </td>

                    {/* Submitted date */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500">{formatDate(ticket.created_at || ticket.createdAt)}</span>
                    </td>

                    {/* Type / Priority */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${priorityCfg.pill}`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-current inline-block opacity-60" />
                        {ticket.priority || ticket.type || 'Low'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DashboardSupportTickets;
